/* -----------------Globals--------------- */
import { isWeb } from "@helpers/platform";

import { MaterialCommunityIcons as Icon } from "@expo/vector-icons";
/* -----------------UI--------------- */
import {
	Card,
	Checkbox,
	Chip,
	IconButton,
	List,
	Icon as PaperIcon,
	RadioButton,
} from "react-native-paper";

/* -----------------Helpers & Hooks--------------- */
import { useAppTheme } from "@hooks/useAppTheme";

import { useAppStore } from "@hooks/useAppStore";
/* -----------------Child components--------------- */
import { Box, MarkdownRenderer, TooltipWrapper } from "@memorang/ui";
import CustomMarkdownRenderer from "components/CustomMarkdownRenderer";
import type { ViewStyle } from "react-native";
import { ListItemContainer } from "./ListItemContainer";

type Props = {
	answered?: boolean;
	stem?: string;
	handleSelectChoice: (crossingOut?: boolean) => void;
	status: "checked" | "unchecked";
	multiSelect?: boolean;
	value?: string;
	isCrossedOut?: boolean;
	isCorrectAnswer?: boolean;
	percentageAnswered?: number;
	useCardWrapper?: boolean;
	showCrossOut?: boolean;
	isSelected?: boolean;
	index: number;
	drag?: () => void;
	disabled?: boolean;
	infoTooltip?: string;
};
const ChoiceListItem = ({
	answered,
	disabled,
	stem,
	handleSelectChoice,
	status,
	multiSelect,
	value,
	isCrossedOut,
	isCorrectAnswer,
	percentageAnswered,
	useCardWrapper = true,
	showCrossOut,
	isSelected,
	index,
	drag,
	infoTooltip,
	// biome-ignore lint/complexity/noExcessiveCognitiveComplexity: <explanation>
}: Props) => {
	const theme = useAppTheme();

	const appFont = useAppStore((store) => store.app.font) || "opensans";

	//TODO:The label props on the Checkbox.Item or RadioBox does not accept a component. It only accepts a string.
	// So we have to create custom component for the Checkbox and RadioBox.

	const SelectionContainer = multiSelect
		? Checkbox.Android
		: RadioButton.Android;
	const showLeft = isWeb || multiSelect || !useCardWrapper;

	const getCardProps = () => {
		const answerType = isCorrectAnswer ? "correct" : "incorrect";

		if (isSelected) {
			const backgroundColor = answered
				? theme.colors.answer[answerType].background
				: theme.colors.primaryContainer;
			const borderColor = answered
				? theme.colors.answer[answerType].border
				: theme.colors.primary;
			return {
				backgroundColor,
				borderColor,
			};
		}

		if (isCorrectAnswer && answered) {
			return {
				backgroundColor: theme.colors.answer[answerType].background,
				borderColor: theme.colors.answer[answerType].border,
			};
		}

		return {
			backgroundColor: undefined,
			borderColor: theme.colors.outlineVariant,
		};
	};

	const getTextColor = () => {
		if (isSelected || (isCorrectAnswer && answered)) {
			return answered ? theme.colors.shadow : theme.colors.text;
		}

		return theme.colors.text;
	};

	const mode = "outlined";

	const CrossOutAnswerIconButton = (props: {
		color: string;
		style?: ViewStyle | undefined;
		isCrossedOut?: boolean;
	}) => (
		<IconButton
			{...props}
			disabled={answered || disabled}
			onPress={() => {
				if (!answered) {
					handleSelectChoice(true);
				}
			}}
			iconColor={theme.colors.onSurfaceVariant}
			icon="close"
			size={24}
			accessibilityLabel={
				props.isCrossedOut ? "Undo cross out answer" : "Cross out answer"
			}
			testID={`cross-out-answer-icon-button-${index}`}
			style={{
				marginRight: -6,
				...(isCrossedOut ? { opacity: 0.38 } : {}),
			}}
		/>
	);

	const chipIcon = isSelected
		? isCorrectAnswer
			? "check"
			: "close"
		: undefined;
	const chipColor = isCorrectAnswer
		? theme.colors.confidence.high
		: theme.colors.confidence.low;

	const PercentageAnsweredChip = ({ value }: { value?: number }) => {
		const tooltip = isSelected
			? `You selected this ${
					isCorrectAnswer ? "correct" : "incorrect"
				} choice along with ${value}% of other people`
			: `${value}% of people selected this ${
					isCorrectAnswer ? "correct" : "incorrect"
				} choice`;
		return (
			<TooltipWrapper title={tooltip}>
				<Chip
					mode="outlined"
					compact
					selected={isSelected}
					icon={() => <Icon name={chipIcon} size={18} color={chipColor} />}
					textStyle={{ color: chipColor, fontFamily: `${appFont}-semibold` }}
					selectedColor={chipColor}
				>
					{value != null && value > 5 ? `${value}%` : "< 1%"}
				</Chip>
			</TooltipWrapper>
		);
	};

	const Wrapper = useCardWrapper ? Card : Box;

	const cardProps = getCardProps();

	const {
		app: { fontSizes },
	} = useAppStore();

	const fontSize = fontSizes?.answer || 16;

	return (
		<Wrapper
			mode={mode}
			style={{
				...(useCardWrapper ? cardProps : {}),
				opacity: disabled && !answered ? 0.5 : 1,
				overflow: "hidden",
			}}
		>
			<ListItemContainer
				handleCrossOut={() => {
					if (!(answered || disabled)) {
						handleSelectChoice(true);
					}
				}}
			>
				<List.Item
					onResponderStart={drag}
					disabled={answered || disabled}
					style={{
						paddingLeft: (useCardWrapper && multiSelect) || isWeb ? 6 : 0,
						paddingRight: useCardWrapper ? 12 : 0,
						paddingVertical: useCardWrapper ? 8 : 0,
						cursor: answered || disabled ? "auto" : "pointer",
					}}
					left={(p) =>
						showLeft ? (
							<Box
								flexDirection="row"
								alignItems="center"
								cursor={answered || disabled ? "default" : "pointer"}
								importantForAccessibility="no-hide-descendants"
								accessible={false}
							>
								{!useCardWrapper && answered && (
									<Icon
										name={isCorrectAnswer ? "check-circle" : "close-circle"}
										size={24}
										color={chipColor}
										disabled
										style={{
											paddingLeft: 8,
										}}
									/>
								)}
								<SelectionContainer
									{...p}
									value={value!}
									status={status}
									color={cardProps.borderColor || theme.colors.onSurfaceVariant}
									disabled={answered || disabled}
									onPress={() => {
										if (!(answered || disabled)) {
											handleSelectChoice();
										}
									}}
								/>
							</Box>
						) : null
					}
					onPress={() => {
						if (!(answered || disabled)) {
							handleSelectChoice();
						}
					}}
					accessibilityRole={multiSelect ? "checkbox" : "radio"}
					accessibilityState={{
						checked: isSelected,
						disabled: answered || disabled,
					}}
					titleNumberOfLines={10}
					title={() => (
						<CustomMarkdownRenderer
							text={stem}
							variant="answer"
							customStyles={{
								text: {
									color: getTextColor(),
								},
								paragraph: {
									fontSize,
									...(isCrossedOut
										? {
												opacity: 0.38, // disabled opacity
												textDecorationLine: "line-through",
											}
										: {}),
								},
							}}
						/>
					)}
					right={(p) => (
						<Box
							{...p}
							height={34}
							minWidth={60}
							flexDirection="row"
							justifyContent="flex-end"
							alignItems="center"
						>
							{infoTooltip ? (
								<TooltipWrapper
									title={
										(
											<MarkdownRenderer
												text={infoTooltip}
												variant="explanation"
												customStyles={{
													text: {
														color: theme.colors.white,
													},
													bullet_list: {
														color: theme.colors.white,
													},
													bullet_list_icon: {
														color: theme.colors.white,
														marginTop: 2,
													},
												}}
											/>
										) as unknown as string
									}
								>
									<PaperIcon
										source="information-outline"
										size={20}
										color={theme.colors.primary}
									/>
								</TooltipWrapper>
							) : null}
							{answered && percentageAnswered ? (
								<PercentageAnsweredChip value={percentageAnswered} />
							) : null}
							{!answered && showCrossOut && !drag && (
								<CrossOutAnswerIconButton {...p} isCrossedOut={isCrossedOut} />
							)}
							{answered && drag && (
								<Icon
									name={
										isCorrectAnswer
											? "check-circle-outline"
											: "close-circle-outline"
									}
									color={
										isCorrectAnswer
											? theme.colors.success.main
											: theme.colors.confidence.low
									}
									size={24}
								/>
							)}
							{!answered && drag && (
								<Icon
									name="drag-vertical"
									color={theme.colors.onSurfaceVariant}
									size={24}
									style={{
										...(isWeb
											? {
													cursor: "grab",
												}
											: {}),
									}}
								/>
							)}
						</Box>
					)}
					testID={`choice-list-item-${index}`}
				/>
			</ListItemContainer>
		</Wrapper>
	);
};

export default ChoiceListItem;
